import { Navigate, useNavigate, useParams } from 'react-router';

import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import { GradebookGrid } from './GradebookGrid';
import { GradebookHeader } from './GradebookHeader';
import { GradebookLoadingBar } from './GradebookLoadingBar';
import { GradesLoadFailed } from './GradesLoadFailed';
import { exportRosterCsv } from './rosterCsv';
import { useGradebook } from './useGradebook';
import styles from './GradebookPage.module.css';

export function GradebookPage() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const gradebook = useGradebook(courseId);

  if (!courseId) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Missing course link</h1>
          <p className={styles.stateMessage}>
            The gradebook route is incomplete, so the course could not be loaded.
          </p>
        </Card>
      </section>
    );
  }

  if (!gradebook.courseQuery.isPending && gradebook.course && gradebook.course.userRole !== 'instructor') {
    return <Navigate replace to={`/app/courses/${courseId}`} />;
  }

  if (gradebook.courseQuery.isPending) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard} role="status">
          <h1 className={styles.stateTitle}>Loading gradebook</h1>
          <p className={styles.stateMessage}>Loading gradebook data.</p>
        </Card>
      </section>
    );
  }

  if (gradebook.courseQuery.isError || !gradebook.course) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard} role="alert">
          <h1 className={styles.stateTitle}>Unable to load the gradebook</h1>
          <p className={styles.stateMessage}>
            {gradebook.courseQuery.error?.message ?? 'Try again in a moment.'}
          </p>
        </Card>
      </section>
    );
  }

  const { course } = gradebook;

  const hasStudents = gradebook.students.length > 0;
  // Only declare the course empty once BOTH the roster and grade queries have
  // settled and the roster reports zero enrolled students. The roster can resolve
  // with a known studentCount while the (paginated) grade rows are still loading,
  // which previously flashed "No students enrolled yet" next to a "0/189" bar.
  const hasNoEnrolledStudents =
    !gradebook.isLoadingRoster &&
    !gradebook.isLoadingGrades &&
    gradebook.studentCount === 0 &&
    !hasStudents;
  // No student rows yet, but the course is not confirmed empty: still loading.
  const isLoadingRosterRows = !hasStudents && !hasNoEnrolledStudents;

  return (
    <section className={styles.page}>
      <GradebookHeader
        actions={
          <Button
            // Only once every grade is in: a spreadsheet of the rows so far, or
            // of a load that failed, would give the rest a score of 0.
            disabled={gradebook.students.length === 0 || !gradebook.gradesComplete}
            onClick={() =>
              exportRosterCsv(course, gradebook.assignments, gradebook.students)
            }
            variant="secondary"
          >
            Download Spreadsheet
          </Button>
        }
        backLabel="Back to Course"
        onBack={() => navigate(`/app/courses/${courseId}`)}
        // No count until the roster is known: "0 students" read as an empty
        // course while it loaded.
        subtitle={
          gradebook.isLoadingRoster
            ? undefined
            : `${gradebook.students.length} student${gradebook.students.length === 1 ? '' : 's'}`
        }
        title={`${gradebook.course.title} — Gradebook`}
      />

      {gradebook.isLoadingGrades &&
      gradebook.loadingProgress &&
      gradebook.loadingProgress.total > 0 ? (
        <GradebookLoadingBar
          loaded={gradebook.loadingProgress.loaded}
          total={gradebook.loadingProgress.total}
        />
      ) : null}

      {gradebook.gradesFailed ? (
        <GradesLoadFailed error={gradebook.gradesError} onRetry={gradebook.retryGrades} />
      ) : hasNoEnrolledStudents ? (
        <Card className={styles.stateCard}>
          <h2 className={styles.stateTitle}>No students enrolled yet</h2>
          <p className={styles.stateMessage}>
            The roster will appear here once students join the course.
          </p>
        </Card>
      ) : isLoadingRosterRows ? (
        <Card className={styles.stateCard} role="status">
          <h2 className={styles.stateTitle}>Loading roster…</h2>
          <p className={styles.stateMessage}>
            Fetching the class roster and grades.
          </p>
        </Card>
      ) : (
        <GradebookGrid
          assignments={gradebook.assignments}
          courseId={courseId}
          loadingProgress={gradebook.loadingProgress}
          onSelectStudent={(studentId) =>
            navigate(`/app/courses/${courseId}/grades/${studentId}`)
          }
          onSort={gradebook.setSortColumn}
          sortColumn={gradebook.sortColumn}
          sortDirection={gradebook.sortDirection}
          students={gradebook.students}
        />
      )}
    </section>
  );
}
